import React, { useEffect, useRef } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HomePage from '@/features/home-v2/components/ui/pages/HomePage';
import { usePostLoginDestination } from '@/features/home-v2/hooks/usePostLoginDestination';
import { useChangeApp } from '@/shared/hooks/useChangeApp';
import { useAuthStore } from '@/features/auth/store/auth';
import type { LoginLocationState } from '@/features/auth/types/auth.types';

/**
 * HomeContainer
 *
 * Single entry point for the portal home route. It is the **only** place
 * where the post-login destination is decided:
 *
 * 0. **Acesso não permitido** — quem autenticou, mas não possui plataforma
 *    associada a nenhuma business account acessível, volta ao login.
 * 1. **Post-login destination (RN07)** — valid for every access flow
 *    (password, MFA and federated), since all of them navigate to `/`:
 *    a user with access to exactly ONE platform is sent straight to it via
 *    the IAM change-app flow (the unified home is not shown — there is no
 *    choice to make, CA01); with more than one platform the unified home
 *    renders and the user chooses (CA02). Unknown platforms fail safe into
 *    the home.
 *
 * ## Flow
 *
 * ```mermaid
 * flowchart TD
 *     A[Route index] --> B[HomeContainer]
 *     B --> C{userInfo carregado?}
 *     C -->|não| L[Spin - sem piscar de conteúdo]
 *     C -->|sim| D{Exatamente 1 plataforma conhecida?}
 *     D -->|sim - RN07/CA01| E[useChangeApp → portal único]
 *     D -->|não - RN07/CA02| F[HomePage]
 * ```
 *
 * If the change-app navigation fails, the mutation's error path notifies the
 * user (useChangeApp) and the unified home renders as fallback — the user is
 * never left without a way out.
 *
 * @returns The home page, a redirect state, or a loading state
 *
 * @example
 * ```tsx
 * <Route index element={<HomeContainer />} />
 * ```
 *
 * @since 1.0.0
 * @category Pages
 */
export const HomeContainer: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const destination = usePostLoginDestination();
  const { mutate: navigateTo, isError: redirectFailed } = useChangeApp();
  // The change-app flow consumes a single-use minitoken — fire exactly once.
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (destination.status === 'redirect' && !hasRedirected.current) {
      hasRedirected.current = true;
      navigateTo(destination.target);
    }
  }, [destination, navigateTo]);

  // Autenticou, mas não há plataforma associada às contas acessíveis. A sessão
  // é encerrada (não há nada que o usuário possa fazer autenticado) e ele
  // volta ao login com a mensagem de falta de permissão. `replace` evita que o
  // botão "voltar" o leve novamente a uma home que não pode usar. Navegue
  // antes do reset para que o PrivateRoute não intercepte a saída e descarte
  // o notice enviado ao login.
  useEffect(() => {
    if (destination.status === 'access-not-granted') {
      const state: LoginLocationState = { notice: 'access-not-granted' };
      navigate('/login', { replace: true, state });
      useAuthStore.getState().reset();
    }
  }, [destination.status, navigate]);

  if (
    destination.status === 'loading' ||
    destination.status === 'access-not-granted'
  ) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t('Completing sign in')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '40vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // RN07/CA01 — única plataforma: nada a escolher; segue direto (o overlay
  // global de navegação cobre a transição). Se a navegação falhar, cai na
  // home unificada como caminho de saída.
  if (destination.status === 'redirect' && !redirectFailed) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t('Navigating to application...')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '40vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return <HomePage />;
};

export default HomeContainer;
